import { DEFAULT_LOCALE, ROUTE_NAMES } from '@constants';
import {
  AcceptedResourceItemType,
  AnyResource,
  AuthenticationMethod,
  Billing,
  Breadcrumb,
  CalendarView,
  CommunityState,
  CommunityStatus,
  CommunitySubscription,
  CommunitySubType,
  Conversation,
  ConversationListElement,
  Message,
  PermissionLevel,
  ResourceType,
  RootState,
  SubscriptionPlanDetails,
  SystemSettings,
  UserSettings,
  UserSubType,
} from '@models';

export type SearchParams = {
  from: Nullable<string>;
  to: Nullable<string>;
  isCustomDate: boolean;
  period: Nullable<number>;
  rid: Nullable<number>;
  resourceType?: ResourceType;
};

export interface LastSearch {
  list: AnyResource[];
  limitList: { limit: number; offset: number; more: boolean };
  params: SearchParams;
  hideShowMore: boolean;
  searchItemVal: string;
}

export interface AppState {
  breadcrumbs: Breadcrumb[];
  billing: {
    billing: Nullable<Billing & { paymentInfo?: Record<string, unknown> }>;
    subscription: Nullable<Partial<CommunitySubscription>>;
    planDetails: Nullable<SubscriptionPlanDetails>;
    newSubscription: {
      billing: Nullable<Record<string, unknown>>;
      paymentInfo: Nullable<Record<string, unknown>>;
    };
  };
  views: {
    roomTreeView?: number;
    defaultView?: number;
    usersView?: number;
    groupsView?: number;
    calendarView?: CalendarView;
  };
  user: UserSettings;
  root: RootState;
  community: CommunityState;
  locale: string;
  navigation: {
    open: boolean;
    route?: ROUTE_NAMES;
  };
  list: {
    items: AcceptedResourceItemType[];
    selected: Record<number, AnyResource>;
    isAllSelected: boolean;
  };
  conversation: {
    list: ConversationListElement[];
    selectedConversation: Nullable<Conversation>;
    messages: Selectable<Message>[];
    isParticipant: boolean;
    isOwner: boolean;
    updateDate: number;
  };
  auth: {
    isLoggedIn: boolean;
    sessionToken: Nullable<string>;
    isUserSessionExpiration: boolean;
  };
  languages: string[];
  url: {
    requestedUrl?: string;
  };
  search: {
    lastSearch: Nullable<LastSearch>;
    query: string;
    params: SearchParams;
    originPath: Nullable<string>;
    isSearchScopedByCurrentResource: boolean;
  };
  ui: {
    isModalOpen: boolean;
    isModalFired?: boolean;
  };
  resource: {
    current: Nullable<Partial<AnyResource>>;
  };
  settings: {
    system: SystemSettings;
    signingRoomConfig?: Record<string, unknown>;
    areZippedFilenamesShortened: boolean;
  };
  sidebar: {
    isExpanded: boolean;
    isVisible: boolean;
  };
}

export const initialState: AppState = {
  breadcrumbs: [],
  billing: {
    billing: null,
    subscription: null,
    planDetails: null,
    newSubscription: {
      billing: null,
      paymentInfo: null,
    },
  },
  views: {
    roomTreeView: 0,
  },
  user: {
    access_token: '',
    communityExpirationDaysCount: 0,
    guest: false,
    member: false,
    shortName: '',
    imageDefined: false,
    username: '',
    communityLoginName: '',
    signature: false,
    functions: {},
    permission: 0,
    roomPermission: 0,
    lastLoginDetails: {},
    billing: false,
    preferences: {},
    samlUser: undefined,
    signatureImageDefault: false,
    conversationNotifications: false,
    signatureCustom: false,
    identificationURL: '',
    maxFileSize: 0,
    testSignaturesExhausted: false,
    signatureEvidenceEidas: [],
    signatureEvidenceZertes: [],
    displayName: '',
    subtype: UserSubType.MEMBER,
    language: '',
    lastLogin: 0,
    authenticationMethod: AuthenticationMethod.PASSWORD_ONLY,
    rootUsersPermission: 0,
    rootUsersId: 0,
    rootRoomsPermission: 0,
    rootRoomsId: 0,
    rootGroupsPermission: 0,
    rootGroupsId: 0,
    communityPermission: 0 as PermissionLevel,
    signingRoomPermission: 0,
    signingRoomId: 0,
    communityId: 0,
    communitySubtype: CommunitySubType.PRODUCTIVE,
    communityName: '',
    communityLogo: false,
    communityReadUploadPermission: false,
    communityReporting: false,
    communitySignature: false,
    communityExpireDate: 0,
    communityTimeout: 0,
    communityStatus: CommunityStatus.ACTIVE,
    defaultView: 0,
    maps: false,
    homonyms: false,
    pagingSize: 0,
    privasphere: false,
    guestCreation: false,
    signatureSeal: false,
    editorDeletePermission: false,
    recentActivities: 0,
    showSigningRoom: false,
    communityOneTimeGuests: undefined,
  },
  root: {
    rootGroupsId: 0,
    rootGroupsPermission: 0,
    rootRoomsId: 0,
    rootRoomsPermission: 0,
    rootUsersId: 0,
    rootUsersPermission: 0,
    signingRoomId: 0,
    signingRoomPermission: 0,
  },
  community: {
    communityId: 0,
    communityPermission: 0 as PermissionLevel,
    communityExpireDate: 0,
    communityName: '',
    communityReporting: false,
    communitySignature: false,
    communityStatus: CommunityStatus.ACTIVE,
    communitySubtype: CommunitySubType.PRODUCTIVE,
    communityTimeout: 0,
  },
  locale: DEFAULT_LOCALE,
  navigation: {
    open: false,
  },
  list: {
    items: [],
    selected: {},
    isAllSelected: false,
  },
  conversation: {
    list: [],
    selectedConversation: null,
    messages: [],
    isParticipant: false,
    isOwner: false,
    updateDate: 0,
  },
  auth: {
    isLoggedIn: false,
    sessionToken: null,
    isUserSessionExpiration: false,
  },
  languages: [],
  search: {
    lastSearch: null,
    query: '',
    params: {
      from: null,
      to: null,
      isCustomDate: false,
      period: null,
      rid: null,
    },
    originPath: null,
    isSearchScopedByCurrentResource: false,
  },
  ui: {
    isModalOpen: false,
  },
  resource: {
    current: null,
  },
  settings: {
    system: {},
    areZippedFilenamesShortened: false,
  },
  url: {},
  sidebar: {
    isExpanded: false,
    isVisible: false,
  },
};
